import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { gameService, checkConsecutiveDays, getCountGameType, getGamePlayedTimeForEarly } from '../../services/gameService';
import { checkAchievements, getUserProgress, setUserProgress } from '../../services/achievementService';
import { supabase } from '../../utils/supabaseClient';

const ScoreSubmission = ({ 
  open, 
  onClose, 
  gameData, 
  scoreData, 
  onSubmitSuccess 
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmitScore = async () => {
    try {
      setSubmitting(true);
      setError(null);
      
      // Add debugging to see what data we're working with
      console.log('Submitting score with game data:', gameData);
      console.log('Score data:', scoreData);

      await gameService.submitScore({
        gameId: gameData.id,
        gameName: gameData.name,
        gameType: gameData.type,
        score: scoreData.score,
        duration: scoreData.duration,
        difficulty: scoreData.difficulty,
        errors: scoreData.errors,
        metadata: {
          ...(scoreData.metadata || {}),
          gameDetails: `${gameData.name} - ${scoreData.difficulty}`
        }
      });

      // After successful score submission, update user progress and check for new achievements
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (userId) {
        // Fetch current stats
        const { data: currentStats } = await getUserProgress(userId);

        // Prepare updated stats by incrementing games_played and adding achievement points
        let updatedStats = {};
        updatedStats.games_played = (currentStats?.games_played || 0) + 1
        if(gameData.type == 'memory'){
          updatedStats.memory_score = scoreData.score;
        }
        if(gameData.type == 'word'){
          updatedStats.word_games = (currentStats?.word_games || 0) + 1
        }
        if(gameData.type == 'pattern'){
          updatedStats.pattern_level = scoreData.level;
        }
        
        if(gameData.type == 'math') {
          updatedStats.math_problems = (currentStats?.math_problems || 0) + scoreData.problemsCompleted;
        }
        if(gameData.type == 'reflex'){
          updatedStats.avg_reaction_time = Math.round(scoreData.averageTime);
        }
        if(scoreData.errors == 0){
          updatedStats.perfect_score = 'TRUE';
        }
        const {data: consecutiveDays} = await checkConsecutiveDays(userId)
        if(consecutiveDays){
          updatedStats.consecutive_days = consecutiveDays;
        }

        const {data: gameTypes} = await getCountGameType(userId)
        if(gameTypes){
          updatedStats.game_types = gameTypes;
        }

        const {data: earlyPlay} = await getGamePlayedTimeForEarly(userId)
        console.log(earlyPlay)
        if(earlyPlay){
          updatedStats.early_play = earlyPlay > 0 ? 'TRUE' : 'FALSE';
        }

        const { data: currentResStats } = await setUserProgress(userId, updatedStats);
        // Check for new achievements
        const newAchievements = await checkAchievements(userId, currentResStats || {});
        if (newAchievements.length > 0) {
          console.log('New achievements earned:', newAchievements);
        }
       
      }

      onSubmitSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error submitting score:', err);
      setError('Failed to submit score. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Game Complete!</DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" gutterBottom>
            {gameData?.name}
          </Typography>
          <Typography variant="body1" gutterBottom>
            Difficulty: {scoreData?.difficulty}
          </Typography>
          <Typography variant="h4" color="primary" gutterBottom>
            Score: {scoreData?.score}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Time: {Math.floor(scoreData?.duration / 60)}m {scoreData?.duration % 60}s
          </Typography>
          {scoreData?.errors > 0 && (
            <Typography variant="body2" color="error">
              Errors: {scoreData.errors}
            </Typography>
          )}
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmitScore} 
          variant="contained" 
          color="primary"
          disabled={submitting}
        >
          {submitting ? <CircularProgress size={24} /> : 'Submit Score'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScoreSubmission;
